<template>
  <wui-modal
    v-click-outside="close"
    :title="$t('mainAside.externalContact')"
    :size="40"
    class="!max-w-[440px]"
    @close="close"
  >
    <div class="p-4 flex gap-3 items-center">
      <contact-avatar
        :contact="contact"
        icon="userM"
        class="h-[100px]! w-[100px]!"
        :contact-type="contact?.type"
      />
      <div>
        <p
          class="text-fg-base-fg3 text-heading-m-18-24 mb-[2px]"
          :class="contact?.name.trim().includes(' ') ? 'break-keep' : 'break-all'"
        >
          {{ contact?.name }}
        </p>
        <p
          class="text-body-r-13-18 text-fg-base-fg22 mb-[2px]"
          :class="contact?.position?.trim().includes(' ') ? 'break-keep' : 'break-all'"
        >
          {{ contact?.position }}
        </p>
        <p
          class="text-body-r-13-18 text-fg-base-fg22 mb-[2px]"
          :class="contact?.organizationalUnit?.trim().includes(' ') ? 'break-keep' : 'break-all'"
        >
          {{ contact?.organizationalUnit }}
        </p>
        <p
          class="text-body-r-13-18 text-fg-base-fg22"
          :class="contact?.organization?.trim().includes(' ') ? 'break-keep' : 'break-all'"
        >
          {{ contact?.organization }}
        </p>
      </div>
    </div>

    <div class="flex mb-[6px] gap-2 px-4 pb-3">
      <wui-btn
        class="grow text-nowrap text-body-m-15-22! bg-surf-neut-soft-def! text-surf-brand-base-def! hover:bg-surf-neut-soft-hov! active:bg-surf-neut-soft-pres!"
        data-test="external-contact-audio-call-btn"
        prepend-icon="phoneM"
        :size="40"
        :title="$t('common.audioCall')"
        @click="call(false)"
      >
        {{ $t('common.audioCall') }}
      </wui-btn>
      <wui-btn
        v-if="isWebcamAvailable && isVideoCodecsEnabled"
        class="grow text-nowrap text-body-m-15-22! bg-surf-neut-soft-def! text-surf-brand-base-def! hover:bg-surf-neut-soft-hov! active:bg-surf-neut-soft-pres!"
        data-test="external-contact-video-call-btn"
        prepend-icon="videoCameraM"
        :size="40"
        :title="$t('common.videoCall')"
        @click="call(true)"
      >
        {{ $t('common.videoCall') }}
      </wui-btn>
    </div>

    <div class="px-4 pb-3">
      <div
        v-if="contact?.mobilePhone || contact?.internalNumber"
        class="grid grid-cols-[100px_auto] items-center gap-x-1 pt-[6px] pb-[10px] border-b border-b-bor-divider mb-1"
      >
        <p class="text-label-r-13-18 text-fg-base-fg22">
          {{ t('common.mobPhone') }}:
        </p>
        <p class="text-body-r-14-20 text-fg-base-fg3">
          {{ contact?.mobilePhone ?? contact?.internalNumber }}
        </p>
      </div>

      <div
        v-if="contact?.email"
        class="cursor-pointer grid grid-cols-[100px_auto] items-center gap-x-1 pt-[6px] pb-[10px] border-b border-b-bor-divider mb-1"
      >
        <p class="text-label-r-13-18 text-fg-base-fg22">
          {{ t('common.email') }}:
        </p>
        <p class="text-body-r-14-20 text-fg-base-fg3">
          <a
            :href="`mailto:${contact?.email}`"
            target="_blank"
          >
            {{ contact?.email }}
            <wui-icon
              name="linkM"
              small
              class="text-fg-base-fg3"
            />
          </a>
        </p>
      </div>
      <div
        v-if="bDay"
        class="grid grid-cols-[100px_auto] items-center gap-x-1 pt-[6px] pb-[10px] border-b border-b-bor-divider mb-1"
      >
        <p class="text-label-r-13-18 text-fg-base-fg22">
          {{ t('common.dateOfBirth') }}:
        </p>
        <p class="text-body-r-14-20 text-fg-base-fg3">
          {{ bDay }}
        </p>
      </div>
      <div class="grid grid-cols-[100px_auto] items-center gap-x-1 py-[6px]">
        <p class="text-label-r-13-18 text-fg-base-fg22">
          {{ groups.length === 1 ? t('common.group') : t('common.groups') }}:
        </p>
        <p class="text-body-r-14-20 text-fg-base-fg3">
          {{ groups }}
        </p>
      </div>
    </div>

    <div class="mt-2">
      <wui-card
        class="w-full border-0! shadow-none!"
        state="text"
        shadow="overflow"
      >
        <wui-list>
          <wui-list-item
            v-if="isMyContact && !readonly"
            icon="editM"
            @click="editContact"
          >
            {{ t('mainAside.editContact') }}
          </wui-list-item>

          <wui-list-item
            icon="downloadM"
            @click="downloadVCard"
          >
            {{ t('mainAside.downloadVCard') }}
          </wui-list-item>

          <wui-list-item
            v-if="isChatMessagingUiEnabled"
            icon="forwardRight"
            @click="openShareContactToChat"
          >
            {{ t('mainAside.shareContactToChat') }}
          </wui-list-item>

          <wui-list-item
            v-if="isMyContact && !readonly"
            icon="trashM"
            variant="negative"
            @click="deleteContact"
          >
            {{ t('mainAside.deleteContact') }}
          </wui-list-item>
        </wui-list>
      </wui-card>
    </div>
  </wui-modal>
</template>

<script setup lang="ts">
import { ClickOutside as vClickOutside, closeDialogKey, safeInject, useDialog, useRenderNotifier, WuiBtn, WuiCard, WuiIcon, WuiList, WuiListItem, WuiModal } from '@wui/common-library'
import dayjs from 'dayjs'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import { ShareContactToChatModal } from '@/widgets/share-contact-to-chat-widget'

import { AddEditUserContactModal } from '@/features/add-edit-user-contact-modal'
import { ConfirmDeleteContactDialog } from '@/features/confirm-delete-contact-dialog'
import { grantMicrophonePermission } from '@/features/mic-permission'

import { useStoreContacts, useStoreContactsFilterable } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'
import { useStoreGroups } from '@/entities/contact-group'
import { useStoreMediaDevices } from '@/entities/media-devices'
import { useContact } from '@/entities/use-contact'
import { useStoreProfile } from '@/entities/user-profile'
import { callFromAnotherTerminalIfNeeded } from '@/entities/user-terminal'

import { useLocalization } from '@/shared/composables/localization'
import { useUserFeatures } from '@/shared/composables/user-features'
import { Contact } from '@/shared/types/api'
import { downloadContactAsVCard } from '@/shared/utils/vcard'

const props = defineProps<{
  contactId: string
  readonly?: boolean
}>()

const closeModal = safeInject(closeDialogKey)

const close = () => {
  if (!childDialogOpened.value) {
    closeModal(true)
  }
}

const storeContacts = useStoreContacts()
const contact = computed(() => storeContacts.findById(props.contactId))

const { callMobile, callMobileVideo, callMobileInternal, callMobileInternalVideo } = useContact(contact.value as Contact)
const { t } = useLocalization()
const { isChatMessagingUiEnabled } = useUserFeatures()
const { isWebcamAvailable } = storeToRefs(useStoreMediaDevices())
const { isVideoCodecsEnabled } = storeToRefs(useStoreProfile())
const storeGroups = useStoreGroups()
const { addOrUpdateContact, deleteContactFromList } = useStoreContactsFilterable()
const { showDialog } = useDialog<Contact>()
const { notifyError } = useRenderNotifier()

const childDialogOpened = ref(false)

const isMyContact = computed(() => {
  return storeContacts.myContacts.findIndex(c => c.id === contact.value?.id) !== -1
})

const groups = computed(() => {
  return contact.value?.groupIds.map(group => storeGroups.groupNamesMap[group]).filter(_ => !!_).join(', ') || t('common.no')
})

const bDay = computed(() => {
  return contact.value?.bDay ? dayjs(contact.value.bDay).format('DD.MM.YYYY') : ''
})

const editContact = async () => {
  const c = contact.value
  if (c === undefined) return
  childDialogOpened.value = true
  const result = await showDialog(AddEditUserContactModal, { contact: c })
  if (result) {
    addOrUpdateContact(result)
  }
  setTimeout(() => { // Чтобы не сработал clickOutside
    childDialogOpened.value = false
  }, 100)
}

const downloadVCard = () => {
  if (contact.value) {
    downloadContactAsVCard(contact.value)
  }
}

const openShareContactToChat = () => {
  if (contact.value) {
    showDialog(ShareContactToChatModal, { contact: contact.value })
  }
}

const deleteContact = async () => {
  if (!contact.value) {
    return
  }
  childDialogOpened.value = true
  const clone = { ...contact.value }
  const response = await showDialog(ConfirmDeleteContactDialog, { contact: contact.value })
  if (response) {
    deleteContactFromList(clone as Contact)
    closeModal(true)
  } else {
    setTimeout(() => {
      childDialogOpened.value = false
    }, 100)
  }
}

const call = async (video = false) => {
  try {
    if (!await grantMicrophonePermission()) {
      return
    }

    if (contact.value?.mobilePhone && await callFromAnotherTerminalIfNeeded(contact.value.mobilePhone)) {
      close()
      return
    }

    if (contact.value?.internalNumber && await callFromAnotherTerminalIfNeeded(contact.value.internalNumber)) {
      close()
      return
    }

    if (contact.value?.internalNumber) {
      await (video ? callMobileInternalVideo() : callMobileInternal())
      close()
      return
    } else {
      await (video ? callMobileVideo() : callMobile())
      close()
      return
    }
    close()
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
    notifyError(errorMessage)
  }
}
</script>
